import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { FileText, Upload, MessageSquareDashed, File, X } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { ManualSolutionCreation } from "@/components/ManualSolutionCreation";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const SolutionCreation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [createMethod, setCreateMethod] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const resetDemo = () => {
    setCreateMethod(null);
    setSelectedFiles([]);
  };

  const handleFileImport = () => {
    toast({
      title: "File import started",
      description: "Your solution documents are being processed"
    });
    setCreateMethod("import");
  };

  const handleGibletCreate = () => {
    toast({
      title: "Giblet assistance requested",
      description: "Giblet will help you create your solution"
    });
    setCreateMethod("giblet");
  };

  const handleManualCreate = () => {
    setCreateMethod("manual");
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...files]);
      toast({
        title: `Selected ${files.length} file(s)`,
        description: files.map(file => file.name).join(", ")
      });
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleContinueToReview = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to upload files",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }

    if (selectedFiles.length === 0) {
      toast({
        title: "No Files Selected",
        description: "Please select at least one file to continue",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload files to Supabase storage
      const uploadedFileData = await Promise.all(
        selectedFiles.map(async (file) => {
          const fileName = `${user.id}/${Date.now()}-${file.name}`;
          
          const { data, error } = await supabase.storage
            .from('solutions')
            .upload(fileName, file);
          
          if (error) throw error;
          
          const { data: { publicUrl } } = supabase.storage
            .from('solutions')
            .getPublicUrl(fileName);
          
          return {
            name: file.name,
            url: publicUrl,
            size: file.size,
            type: file.type
          };
        })
      );

      toast({
        title: "Files uploaded",
        description: "Analyzing content to generate metadata...",
      });

      // Extract text content from the first file
      let extractedContent = '';
      try {
        const firstFile = selectedFiles[0];
        const text = await firstFile.text();
        extractedContent = text.substring(0, 2000);
      } catch (readError) {
        console.error('Failed to read file content:', readError);
      }

      // Try to generate AI metadata
      if (extractedContent.trim().length > 50) {
        try {
          const { data: metadata, error: aiError } = await supabase.functions.invoke('generate-kb-metadata', {
            body: { 
              content: extractedContent,
              contentType: 'solution'
            }
          });

          if (aiError) {
            console.error('AI generation failed:', aiError);
          } else if (metadata) {
            navigate("/solution-review", {
              state: {
                aiSuggestions: {
                  title: metadata.title || selectedFiles[0].name.replace(/\.[^/.]+$/, ""),
                  titleSuggestion: metadata.titleSuggestion,
                  description: metadata.description || "",
                  descriptionSuggestion: metadata.descriptionSuggestion,
                  suggestedDomainIds: metadata.suggestedDomainIds || [],
                  domainConfidence: metadata.domainConfidence || {},
                  overallConfidence: metadata.overallConfidence || 0,
                  domainReasonings: metadata.domainReasonings || []
                },
                uploadedFiles: uploadedFileData,
                sourceMethod: 'file-import'
              }
            });
            setIsUploading(false);
            return;
          }
        } catch (aiError) {
          console.error('AI metadata generation error:', aiError);
        }
      }

      // Fallback: navigate without AI suggestions
      navigate("/solution-review", {
        state: {
          aiSuggestions: {
            title: selectedFiles[0].name.replace(/\.[^/.]+$/, ""),
            description: "",
            suggestedDomainIds: [],
            domainConfidence: {},
            overallConfidence: 0
          },
          uploadedFiles: uploadedFileData,
          sourceMethod: 'file-import'
        }
      });
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif font-bold text-black mb-4">Publish Your Solutions</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto font-serif mb-4">
            Share your innovations and proven solutions with the iSPAI network
          </p>
        </div>

        <div className="mx-auto max-w-2xl">
          <Card className="border-2 border-black">
            <CardHeader className="bg-slate-100 border-b border-black">
              <CardTitle className="font-serif flex items-center">
                <FileText className="mr-2" size={24} />
                Create Solution
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {!createMethod ? (
                <div className="space-y-6">
                  <h3 className="text-xl font-serif text-center mb-6">Choose how to create your solution</h3>
                  
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card className="border-2 hover:border-black hover:shadow-md cursor-pointer transition-all" onClick={handleManualCreate}>
                      <CardContent className="pt-6 pb-6 flex flex-col items-center text-center">
                        <FileText className="h-12 w-12 mb-4 text-black" />
                        <h3 className="font-medium text-lg mb-2">Manual Creation</h3>
                        <p className="text-sm text-gray-600">Build your solution step by step with guided forms</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-2 hover:border-black hover:shadow-md cursor-pointer transition-all" onClick={handleFileImport}>
                      <CardContent className="pt-6 pb-6 flex flex-col items-center text-center">
                        <Upload className="h-12 w-12 mb-4 text-black" />
                        <h3 className="font-medium text-lg mb-2">Import Content</h3>
                        <p className="text-sm text-gray-600">Upload files or import from external sources</p>
                      </CardContent>
                    </Card>
                    
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Card className="border-2 hover:border-black hover:shadow-md cursor-pointer transition-all" onClick={handleGibletCreate}>
                          <CardContent className="pt-6 pb-6 flex flex-col items-center text-center">
                            <MessageSquareDashed className="h-12 w-12 mb-4 text-black" />
                            <h3 className="font-medium text-lg mb-2">Ask Giblet</h3>
                            <p className="text-sm text-gray-600">Let Giblet help you create a solution</p>
                          </CardContent>
                        </Card>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-96 p-4">
                        <div className="space-y-2">
                          <h4 className="font-medium text-blue-800 mb-2">Ask Giblet to Create Your Solution</h4>
                          <div className="flex items-start mb-3">
                            <img src="/lovable-uploads/3da8dedf-9a62-4ffb-8d26-4fd50ad51404.png" alt="Giblet" className="h-12 w-12 mr-3" />
                            <div>
                              <p className="text-sm text-gray-700">
                                I'm Giblet! Tell me what solution you want to publish, and I'll help structure it.
                              </p>
                            </div>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                </div>
              ) : createMethod === "manual" ? (
                <ManualSolutionCreation onCancel={resetDemo} />
              ) : createMethod === "import" ? (
                <div className="space-y-6">
                  <h3 className="text-xl font-serif text-center mb-6">Import Solution Content</h3>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    {selectedFiles.length === 0 ? (
                      <>
                        <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <h4 className="text-lg font-medium mb-2">Drag and drop files here</h4>
                        <p className="text-sm text-gray-500 mb-4">
                          Supported formats: PDF, DOCX, TXT, CSV, MD
                        </p>
                      </>
                    ) : (
                      <>
                        <h4 className="text-lg font-medium mb-4">Selected Documents ({selectedFiles.length})</h4>
                        <div className="max-h-60 overflow-y-auto mb-4">
                          <ul className="space-y-2">
                            {selectedFiles.map((file, index) => (
                              <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                                <div className="flex items-center">
                                  <File className="h-4 w-4 mr-2 text-gray-600" />
                                  <span className="text-sm truncate max-w-xs">{file.name}</span>
                                </div>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeFile(index)}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}
                    <div className="flex justify-center">
                      <Button className="mx-auto" onClick={openFileDialog}>
                        Browse Files
                      </Button>
                      <Input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelection} />
                    </div>
                  </div>
                  
                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="active" onClick={resetDemo}>
                      Start Over
                    </Button>
                    <Button 
                      onClick={handleContinueToReview}
                      disabled={selectedFiles.length === 0 || isUploading}
                    >
                      {isUploading ? "Uploading..." : "Continue to Review"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">Giblet integration coming soon...</p>
                  <Button variant="outline" onClick={resetDemo} className="mt-4">
                    Go Back
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SolutionCreation;
